/**
 * ManifestSchema part helpers (dependency-free).
 *
 * A schema-backed form keeps its manifest in a root ManifestSchema record plus ordered
 * ManifestSchemaPartial records, linked from the RNI by Options.FormManifestSchemaID/Hash
 * (header) and Options.FormFooterManifestSchemaID/Hash (footer). composeParts() merges one
 * record set exactly like the runtime loaders (report_definitions LoadFormManifest,
 * ManifestLoaderService._mergeManifests); splitParts() is its inverse, so a save writes each
 * record back holding only what it owns.
 */

const ROOT_OWNER = 'root';
const MERGED_KEYS = [ 'Descriptors', 'Sections', 'ReferenceManifests' ];

function cloneJSON(pValue)
{
	return (pValue === undefined) ? undefined : JSON.parse(JSON.stringify(pValue));
}

function isPlainObject(pValue)
{
	return Boolean(pValue) && (typeof pValue === 'object') && !Array.isArray(pValue);
}

/**
 * Parse a Manifest / ManifestPartial column; the API may hand it back as an object or a JSON string.
 *
 * @param {*} pValue
 * @return {object|null}
 */
function parseManifestField(pValue)
{
	if (isPlainObject(pValue))
	{
		return pValue;
	}
	if (typeof pValue === 'string' && pValue.trim().length > 0)
	{
		try
		{
			const tmpParsed = JSON.parse(pValue);
			return isPlainObject(tmpParsed) ? tmpParsed : null;
		}
		catch (pError)
		{
			return null;
		}
	}
	return null;
}

function _ref(pID, pHash)
{
	const tmpID = parseInt(pID, 10) || 0;
	const tmpHash = (typeof pHash === 'string') ? pHash.trim() : '';
	return (tmpID > 0 || tmpHash) ? { ID: tmpID, Hash: tmpHash } : null;
}

/**
 * Classify where an RNI's manifest lives, using the runtime's precedence: any FormDefinition
 * object wins and the schema references are ignored.
 *
 * StubFormDefinition flags a schema-backed form whose Options also carry a FormDefinition with no
 * manifest in it (e.g. `{ Product }` left by an older Metadata save) — the runtime loads that stub.
 *
 * @param {object} pOptions - Parsed RNI Options.
 * @return {{ Mode: ('embedded'|'schema'|'none'), Header: ?{ ID: number, Hash: string }, Footer: ?{ ID: number, Hash: string }, IgnoredRefs: boolean, StubFormDefinition: boolean }}
 */
function getManifestStorage(pOptions)
{
	const tmpOptions = isPlainObject(pOptions) ? pOptions : {};
	const tmpHeader = _ref(tmpOptions.FormManifestSchemaID, tmpOptions.FormManifestSchemaHash);
	const tmpFooter = _ref(tmpOptions.FormFooterManifestSchemaID, tmpOptions.FormFooterManifestSchemaHash);
	const tmpHasRefs = Boolean(tmpHeader || tmpFooter);
	const tmpDefinition = isPlainObject(tmpOptions.FormDefinition) ? tmpOptions.FormDefinition : null;
	const tmpDefinitionHasManifest = Boolean(tmpDefinition) &&
		(isPlainObject(tmpDefinition.DefaultFormManifest) || Boolean(tmpDefinition.Descriptors) || Boolean(tmpDefinition.Sections));

	const fResult = (pMode, pExtra) => Object.assign(
		{ Mode: pMode, Header: tmpHeader, Footer: tmpFooter, IgnoredRefs: false, StubFormDefinition: false }, pExtra);

	if (tmpDefinitionHasManifest)
	{
		return fResult('embedded', { IgnoredRefs: tmpHasRefs });
	}
	if (tmpHasRefs)
	{
		return fResult('schema', { StubFormDefinition: Boolean(tmpDefinition) });
	}
	if (tmpDefinition || isPlainObject(tmpOptions.DefaultFormManifest))
	{
		return fResult('embedded');
	}
	return fResult('none');
}

function _partialOwner(pPartialRecord)
{
	return String(pPartialRecord.IDManifestSchemaPartial);
}

function _sectionExists(pSections, pHash)
{
	return pSections.some((pSection) => pSection && pSection.Hash === pHash);
}

/**
 * Merge a root ManifestSchema record with its partials, recording which record supplied each item.
 *
 * @param {object} pRootRecord - ManifestSchema record (`.Manifest`).
 * @param {Array<object>} pPartialRecords - ManifestSchemaPartial records (`.ManifestPartial`), in join Ordinal order.
 * @return {{ Manifest: object, Owners: { Descriptors: Object<string, string>, Sections: Object<string, string>, ReferenceManifests: Object<string, string> } }}
 */
function composeParts(pRootRecord, pPartialRecords)
{
	const tmpManifest = cloneJSON(parseManifestField(pRootRecord && pRootRecord.Manifest)) || {};
	if (!isPlainObject(tmpManifest.Descriptors)) { tmpManifest.Descriptors = {}; }
	if (!Array.isArray(tmpManifest.Sections)) { tmpManifest.Sections = []; }
	if (!isPlainObject(tmpManifest.ReferenceManifests)) { tmpManifest.ReferenceManifests = {}; }

	const tmpOwners = { Descriptors: {}, Sections: {}, ReferenceManifests: {} };
	for (const tmpKey of Object.keys(tmpManifest.Descriptors)) { tmpOwners.Descriptors[tmpKey] = ROOT_OWNER; }
	for (const tmpKey of Object.keys(tmpManifest.ReferenceManifests)) { tmpOwners.ReferenceManifests[tmpKey] = ROOT_OWNER; }
	for (const tmpSection of tmpManifest.Sections)
	{
		if (tmpSection && tmpSection.Hash) { tmpOwners.Sections[tmpSection.Hash] = ROOT_OWNER; }
	}

	for (const tmpPartial of (pPartialRecords || []))
	{
		const tmpSource = parseManifestField(tmpPartial && tmpPartial.ManifestPartial);
		if (!tmpSource) { continue; }
		const tmpOwner = _partialOwner(tmpPartial);

		// First-wins with a falsy check, mirroring the runtime merge.
		for (const [ tmpKey, tmpDescriptor ] of Object.entries(isPlainObject(tmpSource.Descriptors) ? tmpSource.Descriptors : {}))
		{
			if (!tmpManifest.Descriptors[tmpKey])
			{
				tmpManifest.Descriptors[tmpKey] = cloneJSON(tmpDescriptor);
				tmpOwners.Descriptors[tmpKey] = tmpOwner;
			}
		}
		for (const tmpSection of (Array.isArray(tmpSource.Sections) ? tmpSource.Sections : []))
		{
			if (tmpSection && !_sectionExists(tmpManifest.Sections, tmpSection.Hash))
			{
				tmpManifest.Sections.push(cloneJSON(tmpSection));
				if (tmpSection.Hash) { tmpOwners.Sections[tmpSection.Hash] = tmpOwner; }
			}
		}
		for (const [ tmpKey, tmpReference ] of Object.entries(isPlainObject(tmpSource.ReferenceManifests) ? tmpSource.ReferenceManifests : {}))
		{
			if (!tmpManifest.ReferenceManifests[tmpKey])
			{
				tmpManifest.ReferenceManifests[tmpKey] = cloneJSON(tmpReference);
				tmpOwners.ReferenceManifests[tmpKey] = tmpOwner;
			}
		}
	}

	return { Manifest: tmpManifest, Owners: tmpOwners };
}

/**
 * Append a footer manifest onto a header manifest the way the runtime does (additive, first-wins).
 *
 * @param {object} pHeaderManifest
 * @param {object} [pFooterManifest]
 * @return {object} A new merged manifest; the inputs are not mutated.
 */
function composeForm(pHeaderManifest, pFooterManifest)
{
	const tmpManifest = cloneJSON(pHeaderManifest) || {};
	if (!isPlainObject(tmpManifest.Descriptors)) { tmpManifest.Descriptors = {}; }
	if (!Array.isArray(tmpManifest.Sections)) { tmpManifest.Sections = []; }
	if (!isPlainObject(tmpManifest.ReferenceManifests)) { tmpManifest.ReferenceManifests = {}; }
	if (!isPlainObject(pFooterManifest)) { return tmpManifest; }

	for (const [ tmpKey, tmpDescriptor ] of Object.entries(pFooterManifest.Descriptors || {}))
	{
		if (!tmpManifest.Descriptors[tmpKey]) { tmpManifest.Descriptors[tmpKey] = cloneJSON(tmpDescriptor); }
	}
	for (const tmpSection of (Array.isArray(pFooterManifest.Sections) ? pFooterManifest.Sections : []))
	{
		if (tmpSection && !_sectionExists(tmpManifest.Sections, tmpSection.Hash)) { tmpManifest.Sections.push(cloneJSON(tmpSection)); }
	}
	for (const [ tmpKey, tmpReference ] of Object.entries(pFooterManifest.ReferenceManifests || {}))
	{
		if (!tmpManifest.ReferenceManifests[tmpKey]) { tmpManifest.ReferenceManifests[tmpKey] = cloneJSON(tmpReference); }
	}
	return tmpManifest;
}

/**
 * Compose one loaded source — header, footer, or both — into the single manifest a document
 * renders, so every reader of a schema-backed form sees the same thing.
 *
 * @param {{ Parts: object }} pSource - A FormEditorManifestSchemaParts.loadSource() result.
 * @return {object|null} The composed manifest, or null when neither part loaded.
 */
function composeSourceManifest(pSource)
{
	const tmpParts = (pSource && pSource.Parts) || {};
	const tmpHeader = tmpParts.Header && tmpParts.Header.Composition && tmpParts.Header.Composition.Manifest;
	const tmpFooter = tmpParts.Footer && tmpParts.Footer.Composition && tmpParts.Footer.Composition.Manifest;
	if (tmpHeader)
	{
		return composeForm(tmpHeader, tmpFooter);
	}
	return tmpFooter ? cloneJSON(tmpFooter) : null;
}

/**
 * Split an edited manifest back into the records composeParts() read it from.
 *
 * Items go back to the record that supplied them at load. New items join the part that owns their
 * section (a new section joins the part of the section before it), else the root. An item deleted in
 * the edit leaves every record, shadowed copies included, so it cannot resurface on the next load.
 * Non-merged top-level keys belong to the root.
 *
 * @param {object} pEditedManifest
 * @param {object} pRootRecord - The ManifestSchema record as loaded.
 * @param {Array<object>} pPartialRecords - The partial records as loaded, in join Ordinal order.
 * @param {{ Owners: object }} pComposition - composeParts() output from load time.
 * @return {{ Root: object, Partials: Object<string, object> }}
 */
function splitParts(pEditedManifest, pRootRecord, pPartialRecords, pComposition)
{
	const tmpEdited = cloneJSON(pEditedManifest) || {};
	const tmpDescriptors = isPlainObject(tmpEdited.Descriptors) ? tmpEdited.Descriptors : {};
	const tmpSections = Array.isArray(tmpEdited.Sections) ? tmpEdited.Sections : [];
	const tmpReferences = isPlainObject(tmpEdited.ReferenceManifests) ? tmpEdited.ReferenceManifests : {};
	const tmpPartials = pPartialRecords || [];
	const tmpOwners = (pComposition && pComposition.Owners) || { Descriptors: {}, Sections: {}, ReferenceManifests: {} };

	const tmpBuckets = {};
	tmpBuckets[ROOT_OWNER] = { Descriptors: {}, Sections: [], ReferenceManifests: {} };
	for (const tmpPartial of tmpPartials)
	{
		tmpBuckets[_partialOwner(tmpPartial)] = { Descriptors: {}, Sections: [], ReferenceManifests: {} };
	}
	const fKnown = (pOwner) => ((typeof pOwner === 'string') && tmpBuckets[pOwner]) ? pOwner : null;

	// Sections first: descriptors and reference manifests place themselves by section.
	const tmpSectionOwners = {};
	let tmpPrevious = ROOT_OWNER;
	for (const tmpSection of tmpSections)
	{
		const tmpHash = tmpSection && tmpSection.Hash;
		const tmpOwner = (tmpHash && fKnown(tmpOwners.Sections[tmpHash])) || tmpPrevious;
		if (tmpHash) { tmpSectionOwners[tmpHash] = tmpOwner; }
		tmpBuckets[tmpOwner].Sections.push(tmpSection);
		tmpPrevious = tmpOwner;
	}

	const tmpReferenceUse = {};
	for (const tmpSection of tmpSections)
	{
		for (const tmpGroup of ((tmpSection && Array.isArray(tmpSection.Groups)) ? tmpSection.Groups : []))
		{
			const tmpName = tmpGroup && tmpGroup.RecordManifest;
			if (tmpName && !tmpReferenceUse[tmpName]) { tmpReferenceUse[tmpName] = tmpSectionOwners[tmpSection.Hash] || ROOT_OWNER; }
		}
	}
	for (const [ tmpKey, tmpValue ] of Object.entries(tmpReferences))
	{
		const tmpOwner = fKnown(tmpOwners.ReferenceManifests[tmpKey]) || tmpReferenceUse[tmpKey] || ROOT_OWNER;
		tmpBuckets[tmpOwner].ReferenceManifests[tmpKey] = tmpValue;
	}

	for (const [ tmpKey, tmpValue ] of Object.entries(tmpDescriptors))
	{
		const tmpSectionHash = tmpValue && tmpValue.PictForm && tmpValue.PictForm.Section;
		const tmpOwner = fKnown(tmpOwners.Descriptors[tmpKey]) || (tmpSectionHash && tmpSectionOwners[tmpSectionHash]) || ROOT_OWNER;
		tmpBuckets[tmpOwner].Descriptors[tmpKey] = tmpValue;
	}

	// A partial keeps its shadowed copy of a key another record owns, unless the edit deleted the key.
	const tmpEditedHashes = new Set(tmpSections.map((pSection) => pSection && pSection.Hash).filter(Boolean));
	const fKeepShadowed = (pBucket, pSource) =>
	{
		for (const [ tmpKey, tmpValue ] of Object.entries(isPlainObject(pSource.Descriptors) ? pSource.Descriptors : {}))
		{
			if (Object.prototype.hasOwnProperty.call(tmpDescriptors, tmpKey) && !Object.prototype.hasOwnProperty.call(pBucket.Descriptors, tmpKey))
			{
				pBucket.Descriptors[tmpKey] = tmpValue;
			}
		}
		for (const tmpSection of (Array.isArray(pSource.Sections) ? pSource.Sections : []))
		{
			const tmpHash = tmpSection && tmpSection.Hash;
			if (tmpHash && tmpEditedHashes.has(tmpHash) && !_sectionExists(pBucket.Sections, tmpHash))
			{
				pBucket.Sections.push(tmpSection);
			}
		}
		for (const [ tmpKey, tmpValue ] of Object.entries(isPlainObject(pSource.ReferenceManifests) ? pSource.ReferenceManifests : {}))
		{
			if (Object.prototype.hasOwnProperty.call(tmpReferences, tmpKey) && !Object.prototype.hasOwnProperty.call(pBucket.ReferenceManifests, tmpKey))
			{
				pBucket.ReferenceManifests[tmpKey] = tmpValue;
			}
		}
	};

	// Don't add an empty container the record never had.
	const fSetContainers = (pTarget, pBucket, pOriginal) =>
	{
		for (const tmpKey of MERGED_KEYS)
		{
			const tmpValue = pBucket[tmpKey];
			const tmpEmpty = Array.isArray(tmpValue) ? (tmpValue.length === 0) : (Object.keys(tmpValue).length === 0);
			if (!tmpEmpty || (pOriginal && Object.prototype.hasOwnProperty.call(pOriginal, tmpKey)))
			{
				pTarget[tmpKey] = tmpValue;
			}
			else
			{
				delete pTarget[tmpKey];
			}
		}
		return pTarget;
	};

	const tmpRootOriginal = parseManifestField(pRootRecord && pRootRecord.Manifest) || {};
	const tmpRoot = {};
	for (const tmpKey of Object.keys(tmpEdited))
	{
		tmpRoot[tmpKey] = MERGED_KEYS.includes(tmpKey) ? null : tmpEdited[tmpKey];
	}
	fSetContainers(tmpRoot, tmpBuckets[ROOT_OWNER], tmpRootOriginal);

	const tmpPartialManifests = {};
	for (const tmpPartial of tmpPartials)
	{
		const tmpOwner = _partialOwner(tmpPartial);
		const tmpOriginal = parseManifestField(tmpPartial.ManifestPartial) || {};
		fKeepShadowed(tmpBuckets[tmpOwner], tmpOriginal);
		const tmpNext = {};
		for (const tmpKey of Object.keys(tmpOriginal))
		{
			tmpNext[tmpKey] = MERGED_KEYS.includes(tmpKey) ? null : cloneJSON(tmpOriginal[tmpKey]);
		}
		tmpPartialManifests[tmpOwner] = fSetContainers(tmpNext, tmpBuckets[tmpOwner], tmpOriginal);
	}

	return { Root: tmpRoot, Partials: tmpPartialManifests };
}

function _canonical(pValue)
{
	if (Array.isArray(pValue))
	{
		return pValue.map(_canonical);
	}
	if (isPlainObject(pValue))
	{
		const tmpResult = {};
		for (const tmpKey of Object.keys(pValue).sort())
		{
			if (pValue[tmpKey] !== undefined) { tmpResult[tmpKey] = _canonical(pValue[tmpKey]); }
		}
		return tmpResult;
	}
	return pValue;
}

function canonicalJSON(pValue)
{
	return JSON.stringify(_canonical(pValue === undefined ? null : pValue));
}

function _clearRows(pSections)
{
	for (const tmpSection of (Array.isArray(pSections) ? pSections : []))
	{
		for (const tmpGroup of ((tmpSection && Array.isArray(tmpSection.Groups)) ? tmpSection.Groups : []))
		{
			if (tmpGroup) { delete tmpGroup.Rows; }
		}
	}
}

/**
 * Add the bare groups the editor creates when it loads a manifest: one for each descriptor Group its
 * Section does not declare, and a default group for a section with descriptors but no groups. Mirrors
 * pict-section-formeditor's `_ensureSectionGroups` (Pict-Provider-FormEditorManifestOps.js); the two must
 * not drift. The runtime builds the same groups, so adding them changes nothing that renders.
 *
 * @param {object} pManifest - Mutated.
 */
function _addEditorGroups(pManifest)
{
	if (!Array.isArray(pManifest.Sections) || !isPlainObject(pManifest.Descriptors))
	{
		return;
	}
	const tmpSections = {};
	for (const tmpSection of pManifest.Sections)
	{
		if (!isPlainObject(tmpSection))
		{
			continue;
		}
		if (!Array.isArray(tmpSection.Groups))
		{
			tmpSection.Groups = [];
		}
		if (tmpSection.Hash)
		{
			tmpSections[tmpSection.Hash] = tmpSection;
		}
	}
	for (const tmpDescriptor of Object.values(pManifest.Descriptors))
	{
		const tmpPictForm = isPlainObject(tmpDescriptor) ? tmpDescriptor.PictForm : null;
		const tmpSection = (isPlainObject(tmpPictForm) && tmpPictForm.Section) ? tmpSections[tmpPictForm.Section] : null;
		if (!tmpSection)
		{
			continue;
		}
		const tmpGroupHash = tmpPictForm.Group || '';
		if (!tmpGroupHash)
		{
			if (tmpSection.Groups.length === 0)
			{
				tmpSection.Groups.push({ Hash: `${tmpSection.Hash}Group_Default`, Name: 'Default', Layout: 'Record' });
			}
		}
		else if (!tmpSection.Groups.some((pGroup) => pGroup && pGroup.Hash === tmpGroupHash))
		{
			tmpSection.Groups.push({ Hash: tmpGroupHash, Name: tmpGroupHash, Layout: 'Record' });
		}
	}
}

/**
 * Comparable form of a manifest: the groups the editor adds on load included, Group Rows cleared (the
 * platform rebuilds them at load) and empty top-level containers dropped, so a no-op edit compares
 * equal to what was stored.
 *
 * @param {object} pManifest
 * @return {object}
 */
function normalizeForCompare(pManifest)
{
	const tmpManifest = cloneJSON(parseManifestField(pManifest)) || {};
	_addEditorGroups(tmpManifest);
	_clearRows(tmpManifest.Sections);
	for (const tmpReference of Object.values(isPlainObject(tmpManifest.ReferenceManifests) ? tmpManifest.ReferenceManifests : {}))
	{
		if (tmpReference) { _clearRows(tmpReference.Sections); }
	}
	for (const tmpKey of Object.keys(tmpManifest))
	{
		const tmpValue = tmpManifest[tmpKey];
		if ((Array.isArray(tmpValue) && tmpValue.length === 0) || (isPlainObject(tmpValue) && Object.keys(tmpValue).length === 0))
		{
			delete tmpManifest[tmpKey];
		}
	}
	return tmpManifest;
}

function _keyedDiff(pBefore, pAfter)
{
	const tmpBefore = pBefore || {};
	const tmpAfter = pAfter || {};
	const tmpResult = { Added: 0, Removed: 0, Modified: 0 };
	for (const tmpKey of Object.keys(tmpAfter))
	{
		if (!Object.prototype.hasOwnProperty.call(tmpBefore, tmpKey)) { tmpResult.Added++; }
		else if (canonicalJSON(tmpBefore[tmpKey]) !== canonicalJSON(tmpAfter[tmpKey])) { tmpResult.Modified++; }
	}
	for (const tmpKey of Object.keys(tmpBefore))
	{
		if (!Object.prototype.hasOwnProperty.call(tmpAfter, tmpKey)) { tmpResult.Removed++; }
	}
	return tmpResult;
}

function _sectionsByHash(pSections)
{
	const tmpResult = {};
	(Array.isArray(pSections) ? pSections : []).forEach((pSection, pIndex) =>
	{
		tmpResult[(pSection && pSection.Hash) || `#${pIndex}`] = pSection;
	});
	return tmpResult;
}

/**
 * Describe what changes between two versions of one record's manifest.
 *
 * @return {{ Changed: boolean, Descriptors: object, Sections: object, ReferenceManifests: object, SectionOrder: boolean, Settings: boolean }}
 */
function describeRecordChange(pBefore, pAfter)
{
	const tmpBefore = normalizeForCompare(pBefore);
	const tmpAfter = normalizeForCompare(pAfter);
	const tmpSettingsOf = (pManifest) =>
	{
		const tmpSettings = Object.assign({}, pManifest);
		for (const tmpKey of MERGED_KEYS) { delete tmpSettings[tmpKey]; }
		return canonicalJSON(tmpSettings);
	};
	const tmpHashOrder = (pManifest) => (Array.isArray(pManifest.Sections) ? pManifest.Sections : []).map((pSection) => pSection && pSection.Hash);
	const tmpBeforeHashes = tmpHashOrder(tmpBefore);
	const tmpAfterHashes = tmpHashOrder(tmpAfter).filter((pHash) => tmpBeforeHashes.includes(pHash));

	return {
		Changed: canonicalJSON(tmpBefore) !== canonicalJSON(tmpAfter),
		Descriptors: _keyedDiff(tmpBefore.Descriptors, tmpAfter.Descriptors),
		Sections: _keyedDiff(_sectionsByHash(tmpBefore.Sections), _sectionsByHash(tmpAfter.Sections)),
		ReferenceManifests: _keyedDiff(tmpBefore.ReferenceManifests, tmpAfter.ReferenceManifests),
		SectionOrder: canonicalJSON(tmpBeforeHashes.filter((pHash) => tmpAfterHashes.includes(pHash))) !== canonicalJSON(tmpAfterHashes),
		Settings: tmpSettingsOf(tmpBefore) !== tmpSettingsOf(tmpAfter),
	};
}

/**
 * Plan a save: split the edit, find the records that actually change, and check that re-composing
 * the split reproduces the edit (section order across parts is the one thing a split cannot keep).
 *
 * @param {object} pEditedManifest - Already prepared for save (Rows cleared).
 * @param {object} pRootRecord
 * @param {Array<object>} pPartialRecords - In join Ordinal order.
 * @param {{ Owners: object }} pComposition - composeParts() output from load time.
 * @return {{ Root: object, Partials: Object<string, object>, Changes: Array<{ Kind: ('root'|'partial'), Record: object, Manifest: object, Change: object }>, SectionOrderKept: boolean, ContentKept: boolean }}
 */
function planSave(pEditedManifest, pRootRecord, pPartialRecords, pComposition)
{
	const tmpSplit = splitParts(pEditedManifest, pRootRecord, pPartialRecords, pComposition);
	const tmpChanges = [];

	const tmpRootChange = describeRecordChange(pRootRecord && pRootRecord.Manifest, tmpSplit.Root);
	if (tmpRootChange.Changed)
	{
		tmpChanges.push({ Kind: 'root', Record: pRootRecord, Manifest: tmpSplit.Root, Change: tmpRootChange });
	}
	for (const tmpPartial of (pPartialRecords || []))
	{
		const tmpNext = tmpSplit.Partials[_partialOwner(tmpPartial)];
		const tmpChange = describeRecordChange(tmpPartial.ManifestPartial, tmpNext);
		if (tmpChange.Changed)
		{
			tmpChanges.push({ Kind: 'partial', Record: tmpPartial, Manifest: tmpNext, Change: tmpChange });
		}
	}

	const tmpRecomposed = composeParts({ Manifest: tmpSplit.Root },
		(pPartialRecords || []).map((pPartial) => ({ IDManifestSchemaPartial: pPartial.IDManifestSchemaPartial, ManifestPartial: tmpSplit.Partials[_partialOwner(pPartial)] }))).Manifest;
	const tmpExpected = normalizeForCompare(pEditedManifest);
	const tmpActual = normalizeForCompare(tmpRecomposed);
	const tmpHashes = (pManifest) => canonicalJSON((pManifest.Sections || []).map((pSection) => pSection && pSection.Hash));
	const tmpUnordered = (pManifest) =>
	{
		const tmpCopy = Object.assign({}, pManifest);
		tmpCopy.Sections = _sectionsByHash(pManifest.Sections);
		return canonicalJSON(tmpCopy);
	};

	return {
		Root: tmpSplit.Root,
		Partials: tmpSplit.Partials,
		Changes: tmpChanges,
		SectionOrderKept: tmpHashes(tmpExpected) === tmpHashes(tmpActual),
		ContentKept: tmpUnordered(tmpExpected) === tmpUnordered(tmpActual),
	};
}

/**
 * Count what each record of a composition supplies, for the editor's parts list.
 *
 * @param {{ Owners: object }} pComposition
 * @return {Object<string, { Sections: number, Descriptors: number }>} Keyed by owner ('root' or partial ID).
 */
function countOwned(pComposition)
{
	const tmpCounts = {};
	const fBump = (pOwner, pKey) =>
	{
		if (!tmpCounts[pOwner]) { tmpCounts[pOwner] = { Sections: 0, Descriptors: 0 }; }
		tmpCounts[pOwner][pKey]++;
	};
	const tmpOwners = (pComposition && pComposition.Owners) || {};
	for (const tmpOwner of Object.values(tmpOwners.Sections || {})) { fBump(tmpOwner, 'Sections'); }
	for (const tmpOwner of Object.values(tmpOwners.Descriptors || {})) { fBump(tmpOwner, 'Descriptors'); }
	return tmpCounts;
}

module.exports = {
	ROOT_OWNER,
	parseManifestField,
	getManifestStorage,
	composeParts,
	composeForm,
	composeSourceManifest,
	splitParts,
	normalizeForCompare,
	canonicalJSON,
	describeRecordChange,
	planSave,
	countOwned,
};
